import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, TrendingDown, CheckCircle } from "lucide-react";
import { ConnectFooter } from "@/components/connect/ConnectFooter";
import { Logo } from "@/components/ui/Logo";
import { SEOHead } from "@/components/seo/SEOHead";
import { PAGE_SEO, getCanonicalUrl, getBreadcrumbSchema } from "@/config/seo";
import { useCallerCountry } from "@/hooks/useCallerCountry";
import { useCountryRates } from "@/hooks/useCountryRates";

// Carrier rates are indicative competitor benchmarks (external, not ours).
// elefa rates ALWAYS come from the centralised useCountryRates source
// (Supabase voice_routes → same source billing uses).
const COMPARISON_COUNTRIES = [
  { country: "Ethiopia",    flag: "🇪🇹", dial: "251", carrierCents: 220 },
  { country: "Nigeria",     flag: "🇳🇬", dial: "234", carrierCents: 180 },
  { country: "Kenya",       flag: "🇰🇪", dial: "254", carrierCents: 150 },
  { country: "India",       flag: "🇮🇳", dial: "91",  carrierCents: 120 },
  { country: "South Sudan", flag: "🇸🇸", dial: "211", carrierCents: 300 },
  { country: "UK",          flag: "🇬🇧", dial: "44",  carrierCents: 50  },
];

export default function SaveOnCalls() {
  const { formatLocalRate } = useCallerCountry();
  const { all: allRates, formatRate } = useCountryRates();

  // Cheapest live retail cents per dial code — same table the dialer bills against.
  const cheapestByDial = new Map<string, number>();
  for (const r of allRates) {
    if (!r.ratePerMinuteCents || r.ratePerMinuteCents <= 0) continue;
    const prev = cheapestByDial.get(r.countryCode);
    if (prev == null || r.ratePerMinuteCents < prev) {
      cheapestByDial.set(r.countryCode, r.ratePerMinuteCents);
    }
  }

  const COMPARISONS = COMPARISON_COUNTRIES.map((c) => {
    const cents = cheapestByDial.get(c.dial);
    const elefa = cents != null ? formatRate(cents) + "/min" : "Not available";
    const saving = cents != null && cents < c.carrierCents
      ? Math.round((1 - cents / c.carrierCents) * 100) + "%"
      : "Not available";
    return { ...c, carrier: formatLocalRate(c.carrierCents) + "/min", elefa, saving };
  });


  const BENEFITS = [
    "No connection fees - ever",
    "No monthly subscription required",
    "Purchased credit never expires",
    "Rate shown and confirmed before every call",
    "No hidden charges or surcharges",
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={PAGE_SEO.saveOnCalls.title}
        description={PAGE_SEO.saveOnCalls.description}
        keywords={PAGE_SEO.saveOnCalls.keywords}
        canonicalUrl={getCanonicalUrl("/save-on-calls")}
        structuredData={getBreadcrumbSchema([
          { name: "Home", url: "https://elefa.tel/" },
          { name: "Save on International Calls", url: "https://elefa.tel/save-on-calls" },
        ])}
      />

      <nav className="container py-4 sm:py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Logo variant="header" size="md" asLink={false} withIcon fullName />
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/connect-portal/auth"><Button variant="ghost" size="sm">Log In</Button></Link>
          <Link to="/connect-portal/auth"><Button size="sm">Get Started</Button></Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16 sm:py-24 px-4">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <Badge variant="secondary">Save up to 90%</Badge>
          <h1 className="text-4xl sm:text-6xl font-bold tracking-tight">
            Paying Too Much<br />for International Calls?
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Australian carriers charge up to {formatLocalRate(300)}/min for international calls. elefa Connect gives you HD quality for a fraction of the price.
          </p>
          <Link to="/connect-portal/auth">
            <Button size="lg" className="gap-2 mt-4">Start Saving Today <ArrowRight className="h-4 w-4" /></Button>
          </Link>
        </div>
      </section>

      {/* Rate Comparison Table */}
      <section className="py-16 bg-muted/30 border-t">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4">See the Difference</h2>
          <p className="text-center text-muted-foreground mb-10">Typical Australian carrier rates vs elefa Connect</p>
          <div className="max-w-3xl mx-auto overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b">
                  <th className="py-3 px-4 font-semibold">Destination</th>
                  <th className="py-3 px-4 font-semibold text-muted-foreground">Major Carrier</th>
                  <th className="py-3 px-4 font-semibold text-primary">elefa Connect</th>
                  <th className="py-3 px-4 font-semibold">You Save</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISONS.map((c) => (
                  <tr key={c.country} className="border-b last:border-0">
                    <td className="py-4 px-4 font-medium">{c.flag} {c.country}</td>
                    <td className="py-4 px-4 text-muted-foreground line-through">{c.carrier}</td>
                    <td className="py-4 px-4 text-primary font-semibold">{c.elefa}</td>
                    <td className="py-4 px-4">
                      <Badge variant="secondary" className="bg-primary/10 text-primary">
                        <TrendingDown className="h-3 w-3 mr-1" />{c.saving}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-muted-foreground mt-4">elefa rates are live from our rating engine (same source used at billing). Carrier rates are indicative of typical major carrier international add-on rates and may vary. See your carrier for current rates.</p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 border-t">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-10">No Surprises. Just Savings.</h2>
            <ul className="space-y-4">
              {BENEFITS.map((b, i) => (
                <li key={i} className="flex items-center gap-3 text-lg">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary/5 border-t">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Stop Overpaying for Calls Home</h2>
          <p className="text-lg text-muted-foreground mb-8">Stop paying more than you need to. Switch to elefa Connect.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/connect-portal/auth">
              <Button size="lg" className="gap-2">Start Saving <ArrowRight className="h-4 w-4" /></Button>
            </Link>
            <Link to="/connect-portal/rates">
              <Button variant="outline" size="lg">View All Rates</Button>
            </Link>
          </div>
        </div>
      </section>

      <ConnectFooter />
    </div>
  );
}
